import { Colors } from "@jsq/ds/styles";
import {
  DEF_EDGE_OFFSET,
  DEF_EDGE_WIDTH,
  DEF_NODE_MAX_WIDTH,
  MAX_INDENT_LEVEL,
} from "./constants";
import type {
  EdgeConfig,
  EdgeStyle,
  EdgeCoordinates,
  ElementRegistry,
  TreeNodeData,
  TreeConfig,
} from "./types";

export const validatePosition = (treeConfig?: TreeConfig): Required<TreeConfig> => {
  return {
    edgeStart: treeConfig?.edgeStart ?? DEF_EDGE_OFFSET,
    edgeWidth: treeConfig?.edgeWidth ?? DEF_EDGE_WIDTH,
    labelStart: treeConfig?.labelStart ?? DEF_EDGE_OFFSET,
    nodeMaxWidth: treeConfig?.nodeMaxWidth ?? DEF_NODE_MAX_WIDTH,
  };
};

export const getNodeCSSVars = (
  treeConfig: Required<TreeConfig>,
  level: number,
): { edgeOffset: string; maxWidth: string } => {
  const offset = treeConfig.edgeStart + treeConfig.edgeWidth;
  return {
    edgeOffset: `${offset * level}px`,
    maxWidth: `${treeConfig.nodeMaxWidth - offset * Math.min(level, MAX_INDENT_LEVEL)}px`,
  };
};

export const setSVGbounds = (bounds: DOMRect, svgElement: SVGElement): void => {
  svgElement.setAttribute("width", bounds.width.toString());
  svgElement.setAttribute("height", bounds.height.toString());
};

export const setNodeEdges = (
  bounds: DOMRect,
  configs: EdgeConfig[],
  treeConfig: TreeConfig,
  instanceId: string,
  registry: ElementRegistry,
): void => {
  configs.forEach((config, index) => {
    const startElement = registry.get(config.from);
    const endElement = registry.get(config.to);
    const pathElement = registry.get(`edge_path_${instanceId}_${index}`);
    const labelContainer = registry.get(`label_container_${instanceId}_${index}`);
    const labelWrapElement = registry.get(`label_wrapper_${instanceId}_${index}`);
    if (startElement && endElement && pathElement) {
      const coord: EdgeCoordinates = computeCoordinates(
        bounds,
        treeConfig,
        startElement,
        endElement,
        labelWrapElement,
      );
      applyEdgeSpecs(pathElement, coord, config.edgeStyle, labelContainer);
    }
  });
};

const computeCoordinates = (
  bounds: DOMRect,
  treeConfig: TreeConfig,
  startElem: Element,
  endElem: Element,
  labelWrapElement?: Element | null,
): EdgeCoordinates => {
  const configs = validatePosition(treeConfig);
  const containerTop = bounds.top;
  const containerStart = bounds.left;

  const startElemCoord = startElem.getBoundingClientRect();
  const endElemCoord = endElem.getBoundingClientRect();

  const startCoord = {
    x: startElemCoord.left + configs.edgeStart - containerStart,
    y: startElemCoord.bottom - containerTop,
  };

  const endCoord = {
    x: endElemCoord.left - containerStart,
    y: endElemCoord.bottom - endElemCoord.height / 2 - containerTop,
  };

  let labelCoord = null;
  if (labelWrapElement) {
    const labelWrapperCoord = labelWrapElement?.getBoundingClientRect();
    labelCoord = labelWrapperCoord
      ? {
          x: startCoord.x + configs.labelStart,
          y: endCoord.y - labelWrapperCoord.height / 2,
        }
      : null;
  }

  return { startCoord, endCoord, labelCoord };
};

const applyEdgeSpecs = (
  path: Element,
  coord: EdgeCoordinates,
  edgeStyle?: EdgeStyle,
  labelContainer?: Element | null,
): void => {
  path.setAttribute("stroke", edgeStyle?.color ?? Colors.Neutral300);
  path.setAttribute("stroke-width", edgeStyle?.thickness?.toString() ?? "1");
  if (edgeStyle?.dashWidth && edgeStyle?.dashGap) {
    path.setAttribute("stroke-dasharray", `${edgeStyle.dashWidth} ${edgeStyle.dashGap}`);
  } else {
    path.removeAttribute("stroke-dasharray");
  }
  const { startCoord, endCoord, labelCoord } = coord;

  path.setAttribute("d", `M${startCoord.x} ${startCoord.y} V${endCoord.y} H${endCoord.x}`);
  if (labelContainer && labelCoord) {
    labelContainer.setAttribute("x", labelCoord.x.toString());
    labelContainer.setAttribute("y", labelCoord.y.toString());
  }
};

export const buildEdgeConfigs = (
  treeNode: TreeNodeData,
  instanceId: string,
  edgeStyle?: EdgeStyle,
  rootEdgeStyle?: EdgeStyle,
): EdgeConfig[] => {
  const flattenTree = (node: TreeNodeData): TreeNodeData[] => {
    return [
      { ...node, leafNodes: undefined },
      ...(node.leafNodes ? node.leafNodes.flatMap(flattenTree) : []),
    ];
  };

  const flattenTreeNodes = flattenTree(treeNode);
  const rootNodeId: string = treeNode.id;
  const result: EdgeConfig[] = [];
  flattenTreeNodes.forEach((node) => {
    if (node.rootEdgeLabel) {
      result.push({
        from: `elem-${instanceId}-${rootNodeId}`,
        to: `elem-${instanceId}-${node.id}`,
        label: node.rootEdgeLabel,
        edgeStyle: rootEdgeStyle,
      });
    }
    if (node.parentId) {
      result.push({
        from: `elem-${instanceId}-${node.parentId}`,
        to: `elem-${instanceId}-${node.id}`,
        label: node.edgeLabel,
        edgeStyle: edgeStyle,
      });
    }
  });
  return result;
};
